import React from 'react';

import {
  type CapacityDraft,
  type CapacityErrors,
  type CapacityKey,
  setCapacityField,
} from '../capacity';
import { FormField, TextInput } from './form';

const DIMS: { key: CapacityKey; id: string; label: string }[] = [
  { key: 'widthCm', id: 'width', label: 'Lebar (cm)' },
  { key: 'depthCm', id: 'depth', label: 'Dalam (cm)' },
  { key: 'heightCm', id: 'height', label: 'Tinggi (cm)' },
];
const CAPS: { key: CapacityKey; id: string; label: string }[] = [
  { key: 'capacityCbm', id: 'cap-cbm', label: 'Kapasitas (m³)' },
  { key: 'capacityKg', id: 'cap-kg', label: 'Kapasitas (kg)' },
  { key: 'capacityQty', id: 'cap-qty', label: 'Kapasitas (qty)' },
];

/**
 * Dimensions (cm) + capacity (m³ / kg / qty) of a bin. m³ follows the
 * dimensions until it is typed by hand. Inputs carry `id` / `data-testid`
 * `{idPrefix}-width|depth|height|cap-cbm|cap-kg|cap-qty`.
 */
export const CapacityFields: React.FC<{
  value: CapacityDraft;
  onChange: (next: CapacityDraft) => void;
  errors?: CapacityErrors;
  idPrefix: string;
  /** Hint under m³ (default explains the derivation). */
  cbmHint?: React.ReactNode;
  disabled?: boolean;
}> = ({ value, onChange, errors = {}, idPrefix, cbmHint, disabled }) => {
  const field = (f: { key: CapacityKey; id: string; label: string }) => {
    const id = `${idPrefix}-${f.id}`;
    return (
      <FormField
        key={f.key}
        label={f.label}
        htmlFor={id}
        error={errors[f.key] ?? null}
        hint={
          f.key === 'capacityCbm'
            ? (cbmHint ?? 'Terisi otomatis dari dimensi; boleh diubah.')
            : undefined
        }
      >
        <TextInput
          id={id}
          size="md"
          inputMode="decimal"
          autoComplete="off"
          data-testid={id}
          disabled={disabled}
          invalid={!!errors[f.key]}
          value={value[f.key] as string}
          onChange={(v) => onChange(setCapacityField(value, f.key, v))}
          placeholder="—"
        />
      </FormField>
    );
  };
  return (
    <div className="flex flex-col gap-3" data-testid={`${idPrefix}-capacity-fields`}>
      <div className="grid grid-cols-3 gap-3">{DIMS.map(field)}</div>
      <div className="grid grid-cols-3 gap-3">{CAPS.map(field)}</div>
    </div>
  );
};
